import { Box, ButtonBase } from '@mui/material';
import { forwardRef } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, radius, spacing, tap, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

/**
 * FSE LanguageRow (web) — selectable language row.
 *
 * Spec: `Documentation/Claude design files/preview/language-picker.html`;
 * mirrors the prop surface of `packages/ui/src/mobile/components/LanguageRow.tsx`
 * (native name large, English subtitle, ≥64dp). When selected: leaf-50
 * background + 2px leaf-600 border + a filled `check_circle`. Exposes
 * `role="radio"` + `aria-checked` for use inside a LanguagePicker radiogroup.
 *
 * Wraps MUI `ButtonBase`. Owns the public prop surface — consumers never touch
 * MUI's `sx`. Exposed through @fse/ui so consumers never import @mui/material
 * directly (swap-promise).
 */
export interface LanguageRowProps {
  /** Native rendering of the language name (e.g. "isiXhosa", "Sepedi"). */
  native: string;
  /** English name shown as subtitle (e.g. "Xhosa", "Sotho"). */
  english: string;
  /** Whether this language is currently selected. */
  selected: boolean;
  onSelect: () => void;
}

export const LanguageRow = forwardRef<HTMLButtonElement, LanguageRowProps>(
  function LanguageRow({ native, english, selected, onSelect }, ref) {
    const { mode } = useColorMode();
    const sys = mdSysColor[mode];
    return (
      <ButtonBase
        ref={ref}
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        disableRipple
        sx={{
          width: '100%',
          minHeight: tap.lg,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
          px: `${spacing.md}px`,
          py: `${spacing.s3}px`,
          borderRadius: `${radius.lg}px`,
          borderStyle: 'solid',
          borderWidth: selected ? 2 : 1.5,
          borderColor: selected ? sys.secondary : sys.outlineVariant,
          backgroundColor: selected ? sys.secondaryContainer : sys.surface,
          transition: 'background-color 200ms cubic-bezier(0.2,0,0,1)',
          '&:hover': {
            backgroundColor: selected ? sys.secondaryContainer : sys.background,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            component="span"
            sx={{
              fontFamily: typography.fontFamily,
              fontSize: `${typography.titleLarge.fontSize}px`,
              fontWeight: 700,
              lineHeight: 1.1,
              color: sys.onSurface,
            }}
          >
            {native}
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: typography.fontFamily,
              fontSize: `${typography.labelMedium.fontSize}px`,
              fontWeight: 400,
              color: sys.onSurfaceVariant,
              mt: '2px',
            }}
          >
            {english}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            opacity: selected ? 1 : 0,
            transition: 'opacity 200ms cubic-bezier(0.2,0,0,1)',
          }}
        >
          <Icon name="check_circle" size={28} fill color={sys.secondary} />
        </Box>
      </ButtonBase>
    );
  },
);
